import {
	Select,
	SelectTrigger,
	SelectValue,
	SelectContent,
	SelectGroup,
	SelectItem,
} from '@worldcoo/ui/components/select';
import { Flag } from '@worldcoo/ui/components/form/country-flags';
import { useTranslation } from '@/lib/i18n';
import { useLingui } from '@lingui/react/macro';
import { Suspense } from 'react';
import { localeToLanguage } from '@worldcoo/shared/core';
import type { PortalLocale } from '@worldcoo/monolith/portals';
import { cn } from '@worldcoo/ui/lib/utils';
import { usePortalMetadataQuery } from '@/lib/queries-portal';
import { isNotFound } from '@tanstack/react-router';

const languageOptions: { value: PortalLocale; label: string }[] = [
	{ value: 'ca', label: 'Català' },
	{ value: 'de', label: 'Deutsch' },
	{ value: 'en', label: 'English' },
	{ value: 'es', label: 'Español' },
	{ value: 'eu', label: 'Euskera' },
	{ value: 'fr', label: 'Français' },
	{ value: 'gl', label: 'Galego' },
	{ value: 'it', label: 'Italiano' },
	{ value: 'nl', label: 'Nederlands' },
	{ value: 'pt', label: 'Português' },
];

export function LanguageSelect(props: { className?: string }) {
	return (
		<Suspense fallback={<LanguageSelectPrimitive languageOptions={languageOptions} />}>
			<SuspensedLanguageSelect className={props.className} />
		</Suspense>
	);
}

function SuspensedLanguageSelect(props: { className?: string }) {
	const metadata = usePortalMetadataQuery();

	if (isNotFound(metadata)) return null;

	return (
		<LanguageSelectPrimitive
			languageOptions={languageOptions.filter((option) => metadata.languages.includes(localeToLanguage[option.value]))}
			className={props.className}
		/>
	);
}

function LanguageSelectPrimitive(props: { languageOptions: typeof languageOptions; className?: string }) {
	const { t } = useLingui();
	const { language, changeLanguage } = useTranslation();
	if (props.languageOptions.length <= 1) return;

	return (
		<Select onValueChange={changeLanguage} value={language}>
			<SelectTrigger
				className={cn('bg-transparent! border-none text-base shadow-none', props.className)}
				aria-label={t`Select language`}
			>
				<SelectValue className="gap-2">
					<Flag locale={language} />
					{props.languageOptions.find((option) => option.value === language)?.label}
				</SelectValue>
			</SelectTrigger>
			<SelectContent>
				<SelectGroup>
					{props.languageOptions.map(({ value, label }) => (
						<SelectItem key={value} value={value}>
							<Flag locale={value} />
							{label}
						</SelectItem>
					))}
				</SelectGroup>
			</SelectContent>
		</Select>
	);
}
